import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

// Hero Section for Blog Post Page (simplified)
const BlogPostHeroSection = ({ post }) => {
  return (
    <section className="relative min-h-[50vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#111827] to-[#1F2937]">
      <div className="absolute inset-0 overflow-hidden">
        <svg className="absolute inset-0 w-full h-full">
          <defs>
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(255,255,255,0.03)" strokeWidth="1"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
      </div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
        <motion.h1
          className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          {post.title}
        </motion.h1>
        
        <motion.div
          className="flex justify-center items-center text-gray-300 text-sm mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <span>{post.date}</span>
          <span className="mx-2">|</span>
          <span className="inline-block px-2 py-1 text-xs font-semibold text-white bg-[#4F46E5] rounded">{post.category}</span>
        </motion.div>
      </div>
    </section>
  );
};

// Blog Post Content Section (Placeholder)
const BlogPostContentSection = ({ post }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });

  return (
    <section ref={ref} className="py-16 bg-[#F9FAFB]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <p className="text-[#111827] opacity-70 mb-6">{post.excerpt}</p>
          
          {/* Placeholder content */}
          <div className="prose max-w-none">
            <p className="mb-4 text-[#111827]">This is a placeholder for the full content of the blog post. In a complete implementation, this section would contain the detailed article content, potentially fetched from a CMS or database based on the post ID.</p>
            <p className="mb-4 text-[#111827]">Content would include multiple paragraphs, headings, images, and other rich text elements to provide value to readers. For now, this serves as a structural placeholder to demonstrate the layout of a single blog post page.</p>
            <h2 className="text-2xl font-bold text-[#111827] mb-4">Key Takeaways</h2>
            <ul className="list-disc pl-5 space-y-2 text-[#111827]">
              <li>Placeholder for key point 1 related to the blog topic.</li>
              <li>Placeholder for key point 2 related to the blog topic.</li>
              <li>Placeholder for key point 3 related to the blog topic.</li>
            </ul>
          </div>
        </motion.div>
        
        <motion.div
          className="flex justify-between"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Link to="/blog" className="text-[#4F46E5] font-semibold hover:underline">← Back to All Articles</Link>
          <Link to={`/blog/${post.id + 1}`} className="text-[#4F46E5] font-semibold hover:underline">Next Article →</Link>
        </motion.div>
      </div>
    </section>
  );
};

// Footer Component (reused from other pages)
const Footer = () => {
  return (
    <footer className="bg-[#111827] text-white py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row justify-between items-center">
          <div className="mb-4 md:mb-0">
            <span className="text-2xl font-bold">SwiftBid Pro</span>
          </div>
          <div className="flex space-x-6 text-sm">
            <Link to="/privacy-policy" className="hover:text-[#4F46E5] transition-colors">Privacy Policy</Link>
            <Link to="/terms-of-service" className="hover:text-[#4F46E5] transition-colors">Terms of Service</Link>
            <a href="#" className="hover:text-[#4F46E5] transition-colors">Contact</a>
          </div>
        </div>
        <div className="mt-8 text-center text-sm text-gray-400">
          © 2025 SwiftBid Pro. All rights reserved.
        </div>
      </div>
    </footer>
  );
};

// Main Blog Post Page Component
const BlogPostPage = () => {
  const { id } = useParams();
  
  // Placeholder data for the blog post (in a real implementation, this would be fetched dynamically)
  const placeholderPosts = [
    {
      id: 1,
      title: "How to Create Accurate Quotes in Minutes",
      excerpt: "Learn the best practices for creating fast, accurate quotes that win more jobs for your trade business.",
      date: "June 15, 2025",
      category: "Quoting Tips"
    },
    {
      id: 2,
      title: "Top 5 Mistakes Tradespeople Make When Estimating",
      excerpt: "Avoid these common pitfalls to ensure your estimates are professional and profitable.",
      date: "June 10, 2025",
      category: "Business Advice"
    },
    {
      id: 3,
      title: "Using Technology to Streamline Your Trade Business",
      excerpt: "Discover how digital tools like SwiftBidPro can save you hours on paperwork and quoting.",
      date: "June 5, 2025",
      category: "Technology"
    }
  ];
  
  const post = placeholderPosts.find(p => p.id === parseInt(id)) || placeholderPosts[0];

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>{post.title} | SwiftBidPro Blog</title>
        <meta name="description" content={post.excerpt} />
        <meta name="keywords" content="contractor blog, tradesperson tips, quoting software blog, estimate software tips" />
      </Helmet>
      <BlogPostHeroSection post={post} />
      <BlogPostContentSection post={post} />
      <Footer />
    </div>
  );
};

export default BlogPostPage;
